import React from 'react';
import { Link } from 'react-router-dom';
import { PRIVACY_POLICY, TERMS_AND_CONDITIONS, SMS_TERMS, SMS_FAQ, LAST_UPDATED } from '../lib/legalContent';
import rcMark from '../assets/rc-mark.png';

/**
 * Shared chrome for /privacy, /terms, and /sms-terms.
 * Light theme reads as a legal document.
 */
function LegalShell({ title, children }) {
  return (
    <div className="min-h-screen bg-[#fbfaf8] text-[#2a332c] flex flex-col">
      <header className="border-b border-[#e3dcd1] bg-[#fbfaf8]/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/login" className="flex items-center gap-2" data-testid="legal-home-link">
            <img src={rcMark} alt="The Rippers Club" className="w-8 h-8 object-contain" />
            <span className="font-serif text-lg text-[#013523] tracking-tight">The Rippers Club</span>
          </Link>
          <Link
            to="/login"
            className="text-sm text-[#6a766e] hover:text-[#015e3f]"
            data-testid="legal-back-to-signin"
          >
            Back to sign in
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <article className="max-w-3xl mx-auto px-6 py-10">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#015e3f] font-semibold">
            The Rippers Club
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight mt-2">
            {title}
          </h1>
          <p className="text-xs text-[#6a766e] mt-2">Last updated: {LAST_UPDATED}</p>
          <div className="mt-8 space-y-6 text-[15px] leading-relaxed">
            {children}
          </div>
        </article>
      </main>

      <footer className="border-t border-[#e3dcd1] bg-[#fbfaf8] mt-12">
        <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-center gap-4 px-6 py-4 text-xs text-[#6a766e]">
          <Link to="/privacy" className="hover:text-[#015e3f]" data-testid="footer-privacy-link">Privacy Policy</Link>
          <span className="text-[#e3dcd1]">•</span>
          <Link to="/terms" className="hover:text-[#015e3f]" data-testid="footer-terms-link">Terms & Conditions</Link>
          <span className="text-[#e3dcd1]">•</span>
          <Link to="/sms-terms" className="hover:text-[#015e3f]" data-testid="footer-sms-terms-link">SMS Terms</Link>
        </div>
      </footer>
    </div>
  );
}

/** Renders one section from the legalContent schema (intro + bullets + body). */
function Section({ heading, intro, bullets, body }) {
  return (
    <section className="space-y-2">
      {heading && (
        <h2 className="font-serif text-2xl text-[#013523] tracking-tight mt-8 mb-2">{heading}</h2>
      )}
      {intro && <p>{intro}</p>}
      {bullets && (
        <ul className="list-disc pl-5 space-y-1.5">
          {bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
      {body && <p>{body}</p>}
    </section>
  );
}

function renderSections(sections) {
  return sections.map((s, i) => <Section key={i} {...s} />);
}

export function PrivacyPolicyPage() {
  return <LegalShell title="Privacy Policy">{renderSections(PRIVACY_POLICY)}</LegalShell>;
}

export function TermsOfServicePage() {
  return <LegalShell title="Terms & Conditions">{renderSections(TERMS_AND_CONDITIONS)}</LegalShell>;
}

/* ─────────────────────────────────────────────────────────────────────
 * SMS Terms & Compliance page — built for Twilio A2P 10DLC review.
 * Bespoke sections: opt-in language, visible-checkbox mockup, sample
 * messages styled as text-message bubbles. All visible on a single
 * scroll, no auth required.
 * ───────────────────────────────────────────────────────────────────── */

function H2({ children, testId }) {
  return (
    <h2
      data-testid={testId}
      className="font-serif text-2xl text-[#013523] tracking-tight mt-10 mb-2"
    >
      {children}
    </h2>
  );
}

/**
 * Faithful recreation of the actual /join opt-in row. Uses real
 * <input type="checkbox"> elements (disabled, unchecked) so a Twilio
 * reviewer can SEE the unchecked state in their own browser — no
 * static image needed and no JS state can flip it.
 */
function OptInMockup() {
  return (
    <div
      data-testid="sms-optin-mockup"
      className="rounded-2xl border border-[#e3dcd1] bg-white p-5 shadow-[0_8px_24px_rgba(1,53,35,0.05)]"
    >
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#6a766e] mb-3">
        Live recreation of the /join onboarding flow
      </p>

      {/* Phone field */}
      <label className="text-[11px] font-sans tracking-[0.18em] uppercase text-[#6a766e] mb-1.5 block">
        Phone Number *
      </label>
      <input
        type="tel"
        value="(555) 123-4567"
        readOnly
        aria-label="Sample phone number"
        className="w-full bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl px-4 py-3 text-[#013523] mb-3"
      />

      {/* Opt-in card matching JoinPage styling */}
      <div className="mt-3 rounded-xl border border-[#e3dcd1] bg-[#fbfaf8] p-4 space-y-3">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={false}
            readOnly
            aria-label="SMS notifications opt-in checkbox (shown unchecked by default)"
            data-testid="mockup-sms-checkbox"
            className="mt-0.5 w-4 h-4 rounded border-[#9aa39d] bg-white"
          />
          <span className="text-[12px] leading-relaxed text-[#2a332c]">
            I agree to receive SMS notifications from The Rippers Club related to
            account activity, ladder challenges, match updates, and reminders.
            Message frequency may vary. Message and data rates may apply. Reply
            STOP to opt out or HELP for help.
          </span>
        </label>
        <p className="text-[11px] text-[#6a766e]">
          <strong>Note:</strong> the checkbox above is shown in its actual default state on
          the live registration page — unchecked. The user must manually
          check it to enroll in SMS.
        </p>
      </div>
    </div>
  );
}

/**
 * Sample text-message bubble. Mimics a generic mobile-OS bubble
 * (iMessage-ish but neutral) so it's instantly recognizable as a
 * text message in compliance screenshots.
 */
function MessageBubble({ children, testId }) {
  return (
    <div data-testid={testId} className="flex">
      <div className="max-w-[88%] sm:max-w-[80%] bg-[#015e3f] text-white rounded-2xl rounded-bl-md px-4 py-2.5 shadow-[0_4px_12px_rgba(1,94,63,0.18)]">
        <p className="text-[13px] leading-snug">{children}</p>
      </div>
    </div>
  );
}

const SAMPLE_MESSAGES = [
  {
    id: 'challenge',
    label: 'Challenge notification',
    text: 'The Rippers Club: Aaron challenged you on The Rippers Club ladder. View details: [link] Reply STOP to opt out.',
  },
  {
    id: 'accepted',
    label: 'Match-accepted update',
    text: 'The Rippers Club: Your challenge with Aaron was accepted. Match details: [link] Reply STOP to opt out.',
  },
  {
    id: 'score',
    label: 'Score-submitted notification',
    text: 'The Rippers Club: Aaron submitted a match score. Review: [link] Reply STOP to opt out.',
  },
  {
    id: 'invite',
    label: 'Ladder invitation',
    text: 'The Rippers Club: You have been invited to join The Rippers Club ladder. Register here: [link] Reply STOP to opt out.',
  },
];

export function SmsTermsPage() {
  return (
    <LegalShell title="SMS Terms & Compliance">
      {/* Opening positioning statement so a reviewer instantly knows what the platform is. */}
      <section data-testid="sms-platform-overview" className="rounded-2xl bg-[#015e3f]/5 border border-[#015e3f]/15 px-5 py-4">
        <p className="text-[15px] leading-relaxed text-[#013523]">
          The Rippers Club is a private tennis ladder and country club engagement
          platform where members can participate in ladders, challenges,
          matches, events, and club activities.
        </p>
      </section>

      {/* 1) How Users Opt In — extremely explicit wording */}
      <H2 testId="sms-section-opt-in">How Users Opt In</H2>
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong>SMS consent is optional.</strong> It is <strong>not required</strong> to create an account
          or use The Rippers Club platform.
        </li>
        <li>
          Users opt in during onboarding by voluntarily entering their phone
          number and selecting the optional <strong>SMS Notifications</strong> checkbox.
        </li>
        <li>
          The <strong>SMS Notifications</strong> checkbox is <strong>not pre-selected</strong>.
          Users must affirmatively check the box to enroll.
        </li>
        <li>
          Users can also enable or disable SMS notifications at any time from
          <strong> Settings → Notification Preferences</strong> after their account is created.
        </li>
        <li>
          Consent is logged with a server-side timestamp, IP address, and the
          version of the disclosure the user agreed to.
        </li>
      </ul>

      {/* 1b) Disclosures the reviewer will look for, surfaced together */}
      <H2 testId="sms-section-disclosures">Required Disclosures</H2>
      <ul className="list-disc pl-5 space-y-2">
        <li><strong>Message types:</strong> Challenge notifications, match confirmations, score updates, reminders, account alerts.</li>
        <li><strong>Message frequency:</strong> Varies based on ladder activity.</li>
        <li><strong>Msg &amp; data rates:</strong> May apply per your mobile carrier plan.</li>
        <li><strong>STOP / HELP:</strong> Reply <strong>STOP</strong> to opt out at any time. Reply <strong>HELP</strong> for help.</li>
        <li><strong>Not required:</strong> SMS consent is <strong>not required</strong> to create or use a Ladder account.</li>
      </ul>

      {/* 2) Visual proof of the opt-in flow */}
      <H2 testId="sms-section-mockup">Opt-In Visual Proof</H2>
      <p>
        Below is a live recreation of the registration form sourced from the
        same React component used by /join. The checkbox is shown in its
        actual default state.
      </p>
      <OptInMockup />

      {/* 3) Message Types */}
      <H2 testId="sms-section-message-types">Message Types</H2>
      <p>
        The Rippers Club only sends transactional SMS notifications related to user
        activity within the platform, including:
      </p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Challenge notifications</li>
        <li>Match updates</li>
        <li>Score notifications</li>
        <li>Invite notifications</li>
      </ul>
      <p className="rounded-xl bg-[#c2a251]/10 border border-[#c2a251]/30 px-4 py-3 text-[14px]">
        <strong>The Rippers Club does not send promotional or marketing SMS messages.</strong>
      </p>

      {/* 4) Sample Messages */}
      <H2 testId="sms-section-samples">Sample Messages</H2>
      <p>
        Verbatim examples of the four message types members may receive once
        they have opted in.
      </p>
      <div className="space-y-3" data-testid="sms-sample-messages">
        {SAMPLE_MESSAGES.map((m) => (
          <div key={m.id} className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#6a766e]">
              {m.label}
            </p>
            <MessageBubble testId={`sample-${m.id}`}>{m.text}</MessageBubble>
          </div>
        ))}
      </div>

      {/* 5) Standard SMS terms — render from the shared schema so the
          regulatory text stays the single source of truth. */}
      <H2 testId="sms-section-details">SMS Program Details</H2>
      {SMS_TERMS.map((s, i) => <Section key={i} {...s} />)}

      {/* 6) Disclosure block lifted exactly per spec */}
      <H2 testId="sms-section-disclosure">Disclosure</H2>
      <p className="rounded-xl bg-[#fbfaf8] border border-[#e3dcd1] px-4 py-3 text-[14px]">
        Message frequency varies. Message and data rates may apply. Reply
        <strong> HELP</strong> for help. Reply <strong>STOP</strong> to opt out.
      </p>

      {/* 7) FAQ — Twilio-friendly question/answer pairs */}
      <H2 testId="sms-section-faq">Frequently Asked Questions</H2>
      <div className="space-y-4" data-testid="sms-faq-list">
        {SMS_FAQ.map((item, i) => (
          <div
            key={i}
            data-testid={`sms-faq-item-${i}`}
            className="rounded-xl border border-[#e3dcd1] bg-white px-4 py-3"
          >
            <p className="font-serif text-[15px] text-[#013523] mb-1">
              <span className="text-[#015e3f] font-semibold mr-1.5">Q:</span>
              {item.q}
            </p>
            <p className="text-[14px] text-[#2a332c] leading-relaxed">
              <span className="text-[#015e3f] font-semibold mr-1.5">A:</span>
              {item.a}
            </p>
          </div>
        ))}
      </div>

      {/* 8) Support */}
      <H2 testId="sms-section-support">Support</H2>
      <p>
        For support regarding SMS notifications, contact support through The
        Ladder platform.
      </p>
    </LegalShell>
  );
}
